import {gql} from '@apollo/client';
import React, {useEffect, useState} from 'react';

import * as analytics from 'analytics/Analytics';
import {BulkArchiveFilesOptions} from 'archive/ArchiveActions';
import {getArchiveAction, getUnarchiveAction} from 'archive/ArchiveActionsUtil';
import * as ArchiveModal from 'archive/components/modals/ArchiveModal';
import {ActionMethod, ActionOrigin, FORMS_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {
  FormDefinitionQuery,
  FormDefinitionQueryVariables,
} from 'forms/form-definition/__generated/FormDefinitionQuery';
import {FormDefinitionContent, FormActionConfigInput} from 'forms/form-designer/Constants';
import {CREATE_TASK_ACTION} from 'forms/form-designer/FormDesigner';
import {
  renameForm,
  updateFormDefinition,
  updateFormTemplateCollection,
} from 'forms/form-designer/FormDesignerDAO';
import {convertFormDefinitionToFormDesignerState} from 'forms/form-designer/FormDesignerState';
import {
  FormActionForSchema,
  FormActionForSchemaVariables,
} from 'forms/form-designer/graphql/__generated/FormActionForSchema';
import {FORM_ACTION_QUERY} from 'forms/form-designer/graphql/FormDesignerQueries';
import {FormFixAnalytics} from 'forms/FormsAnalytics';
import {trackFormArchive, trackFormUnarchive} from 'forms/FormsAnalytics';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import TemplateActions from 'notebook/entry-templates/TemplateActions';
import notifications from 'notifications';
import {processTaskSchemaURL} from 'processes/dao/ProcessSchemaDAO';
import getTestProps from 'test/lib/cypress/getTestProps';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import {withLoadingScreen} from 'util/Promises';

import SharedFormDefinitionPage, {getFormDefinitionActionOrigin} from './SharedFormDefinitionPage';

export function InaccessibleFormWarning(): JSX.Element {
  return (
    <span>
      The form you were trying to access is no longer available. It may have been deleted or moved into a
      collection you do not have access to.
    </span>
  );
}

export const FORM_DEFINITION_QUERY = gql`
  query FormDefinitionQuery($formDefinitionId: StrictString!) @skipAudit @engComponent(name: FORMS) {
    formDefinition(id: $formDefinitionId) {
      ... on FormDefinitionNode {
        id
        name
        isValid
        templateCollection {
          id
        }
        latestFormDefinitionVersion {
          id
          version
          actionConfigs {
            actionId
            schema {
              id
              name
              fields
              allFields
              executionType
              canSetAssigneeOnTaskCreation
            }
            sectionKey
            parameters {
              actionParameterId
              formFieldKey
            }
          }
          definition {
            sections
          }
        }
        archiveRecord {
          purpose
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export interface EditFormDefinitionPageProps {
  formDefinitionId: string;
}

export default function EditFormDefinitionPage({formDefinitionId}: EditFormDefinitionPageProps): JSX.Element {
  const [sessionId] = useState(analytics.makeAnalyticsEventId());
  const [name, setName] = useState('');
  const [templateCollectionId, setTemplateCollectionId] = useState('');
  const [archivePurpose, setArchivePurpose] = useState<string | null>(null);
  const [isValid, setIsValid] = useState(false);

  // Keep track of initial values only for analytics
  const [initialName, setInitialName] = useState('');
  const [initialTemplateCollectionId, setInitialTemplateCollectionId] = useState('');
  const [taskSchemaId, setTaskSchemaId] = useState('');

  // Analytics event only gets called once since dependencies are only on URL parameters and sessionid.
  useEffect(() => {
    if (initialName && initialTemplateCollectionId && taskSchemaId) {
      analytics.trackEvent(FORMS_ANALYTICS_ACTIONS.OPENED_FORM_DESIGNER, {
        session_id: sessionId,
        action_method: ActionMethod.UPDATE_FORM_DEFINITION,
        action_origin: getFormDefinitionActionOrigin(),
        form_definition_id: formDefinitionId,
        name: initialName,
        template_collection_id: initialTemplateCollectionId,
        task_schema_id: taskSchemaId,
      });
    }
  }, [sessionId, formDefinitionId, initialName, initialTemplateCollectionId, taskSchemaId]);

  async function updateFormName(name: string): Promise<void> {
    await withLoadingScreen(renameForm(formDefinitionId, name), 'Updating form name...');
    setName(name);
  }

  async function moveForm(templateCollectionId: string): Promise<void> {
    await withLoadingScreen(
      updateFormTemplateCollection(formDefinitionId, templateCollectionId),
      'Moving form...'
    );
    TemplateActions.activateCollection(templateCollectionId);
    setTemplateCollectionId(templateCollectionId);
  }

  // Update form with success toast.
  async function onUpdateFormDefinition(
    formDefinition: FormDefinitionContent,
    formActionConfigs: Array<FormActionConfigInput>,
    autofixUpdates: Array<FormFixAnalytics>,
    userChanges: boolean
  ): Promise<boolean> {
    if (!isValid) {
      // Fixing an invalid form
      setIsValid(true);

      analytics.trackEvent(FORMS_ANALYTICS_ACTIONS.FIXED_INVALID_FORM_DEFINITION, {
        session_id: sessionId,
        action_method: ActionMethod.UPDATE_FORM_DEFINITION,
        action_origin: ActionOrigin.FORMS_EDIT_FORM_DEFINITION_DESIGNER,
        form_definition_id: formDefinitionId,
        task_schema_id: taskSchemaId,
        autofix_changes: autofixUpdates,
        additional_user_changes: userChanges,
      });
    }

    await withLoadingScreen(
      updateFormDefinition(formDefinitionId, formDefinition, formActionConfigs),
      'Updating form...'
    );
    notifications.success(`Updated form "${name}"`);

    return true;
  }

  function onArchive(): void {
    const item: ArchiveModal.ArchiveModalItem = {
      id: formDefinitionId,
      name,
    };
    ArchiveModal.open({
      items: [item],
      onArchive: (templateIds: Array<string>, purpose: string, options: BulkArchiveFilesOptions): void => {
        getArchiveAction(templateIds)(templateIds, purpose, options, () => {
          setArchivePurpose(purpose);
          trackFormArchive(templateIds, {
            action_origin: ActionOrigin.FORMS_EDIT_FORM_DEFINITION_DESIGNER,
            session_id: sessionId,
          });
        });
      },
    });
  }

  function onUnarchive(): void {
    getUnarchiveAction([formDefinitionId])([formDefinitionId], undefined, () => {
      setArchivePurpose(null);
      trackFormUnarchive([formDefinitionId], {
        action_origin: ActionOrigin.FORMS_EDIT_FORM_DEFINITION_DESIGNER,
        session_id: sessionId,
      });
    });
  }

  return (
    <div className="u-height-full" {...getTestProps({component: 'EditFormDefinitionPage', element: 'root'})}>
      <BenchlingQuery<FormDefinitionQuery, FormDefinitionQueryVariables>
        query={FORM_DEFINITION_QUERY}
        renderLoading={() => <CenteredHorizontalThrobber />}
        variables={{formDefinitionId}}
        fetchPolicy="network-only"
        onCompleted={({formDefinition}) => {
          if (!isInaccessible(formDefinition)) {
            setName(formDefinition.name);
            setTemplateCollectionId(formDefinition.templateCollection.id);
            TemplateActions.activateCollection(formDefinition.templateCollection.id);
            setArchivePurpose(formDefinition.archiveRecord?.purpose ?? null);
            setIsValid(formDefinition.isValid);

            // Keep track of initial values only for analytics
            setInitialName(formDefinition.name);
            setInitialTemplateCollectionId(formDefinition.templateCollection.id);
            setTaskSchemaId(formDefinition.latestFormDefinitionVersion.actionConfigs[0].schema!.id);
          }
        }}
      >
        {({formDefinition}) => {
          if (isInaccessible(formDefinition)) {
            return <InaccessibleFormWarning />;
          } else {
            const taskSchema = formDefinition.latestFormDefinitionVersion.actionConfigs[0].schema!;

            return (
              <BenchlingQuery<FormActionForSchema, FormActionForSchemaVariables>
                query={FORM_ACTION_QUERY}
                renderLoading={() => <CenteredHorizontalThrobber />}
                variables={{actionIdentifier: CREATE_TASK_ACTION.identifier, schemaId: taskSchema.id}}
                fetchPolicy="network-only"
              >
                {({formAction}) => {
                  if (isInaccessible(formAction)) {
                    return <InaccessibleFormWarning />;
                  }

                  const [formDesignerState, autofixUpdates] = convertFormDefinitionToFormDesignerState(
                    formDefinition.latestFormDefinitionVersion,
                    [formAction],
                    {
                      lockAction: true,
                      lockSchema: true,
                    }
                  );
                  const actionToSchemas = {
                    [CREATE_TASK_ACTION.identifier]: [taskSchema],
                  };

                  return (
                    <SharedFormDefinitionPage
                      id={formDefinition.id}
                      name={name || formDefinition.name}
                      setName={updateFormName}
                      templateCollectionId={templateCollectionId || formDefinition.templateCollection.id}
                      setTemplateCollectionId={moveForm}
                      archivePurpose={archivePurpose ?? undefined}
                      onArchive={onArchive}
                      onUnarchive={onUnarchive}
                      onSave={(formDefinition, formActionConfigs, userChanges) =>
                        onUpdateFormDefinition(
                          formDefinition,
                          formActionConfigs,
                          autofixUpdates.map((u) => u.toAnalyticsString()),
                          userChanges
                        )
                      }
                      saveButtonText="Save"
                      initialFormDesignerState={formDesignerState}
                      autofixTaskSchemaName={taskSchema.name}
                      autofixTaskSchemaURL={processTaskSchemaURL(taskSchema.id)}
                      autofixUpdates={isValid ? undefined : autofixUpdates}
                      actionToSchemas={actionToSchemas}
                      sessionId={sessionId}
                      actionOrigin={ActionOrigin.FORMS_EDIT_FORM_DEFINITION_DESIGNER}
                    />
                  );
                }}
              </BenchlingQuery>
            );
          }
        }}
      </BenchlingQuery>
    </div>
  );
}
